import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconPartyPopper,
  IconLightningBolt,
  IconScale,
  IconHelpingHand,
  IconInfoCircleOutline,
  IconArrowRight,
} from '@iwoca/icons';
import { Trans } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';

import styles from './ResultScreen.module.css';
import { useLocale } from '../../../hooks/useLocale';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { ExitToSignupLink } from '../components/ExitToSignupLink';
import { OUTCOMES, TOutcomeKey } from '../logic/outcomes.config';

const OUTCOME_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  trophy: IconPartyPopper,
  spark: IconLightningBolt,
  compass: IconScale,
  heart: IconHelpingHand,
  info: IconInfoCircleOutline,
};

type TProps = {
  result: { key: TOutcomeKey; meter: number };
  onRestart: () => void;
};

export const ResultScreen = ({ result, onRestart }: TProps) => {
  const { i18n } = useLocale();
  const navigate = useNavigate();
  const outcome = OUTCOMES[result.key];
  const OutcomeIcon = OUTCOME_ICONS[outcome.icon];

  return (
    <div className={styles.container}>
      <div
        className={`${styles.outcomeHead} ${styles[`accent_${outcome.accent}`] ?? ''}`}
      >
        <span className={styles.chip}>{i18n._(outcome.chip)}</span>
        <div className={styles.titleRow}>
          {OutcomeIcon && <OutcomeIcon className={styles.outcomeIcon} />}
          <h2 className={styles.outcomeTitle}>{i18n._(outcome.title)}</h2>
        </div>
        <p className={styles.outcomeBody}>{i18n._(outcome.body)}</p>
      </div>

      <ul className={styles.points}>
        {outcome.points.map((point) => {
          const text = i18n._(point);
          return (
            <li key={text} className={styles.point}>
              {text}
            </li>
          );
        })}
      </ul>

      <Button
        variant="primary"
        href="/kreditwunsch/neu"
        className={styles.signupCta}
        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
          // Client-side nav so the in-memory DE locale survives the handoff;
          // a full reload re-resolves locale from the cookie, which is unset outside .de hosts.
          event.preventDefault();
          tracking.ampli.creditCheckResultCtaClicked({
            credit_check_outcome: result.key,
            meter: result.meter,
            ...getAmplitudeDefaultEventProperties(),
          });
          navigate('/kreditwunsch/neu');
        }}
      >
        {i18n._(outcome.cta)}
        <IconArrowRight />
      </Button>

      <Button
        variant="tertiary"
        onClick={onRestart}
        className={styles.restartButton}
        type="button"
      >
        <Trans>Start over</Trans>
      </Button>

      <ExitToSignupLink phase="result" />
    </div>
  );
};
